// src/pages/AudienceMode.jsx
import React, { useState, useEffect } from 'react';
import { Search, X, Heart } from 'lucide-react';
import { FaInstagram, FaFacebook, FaYoutube, FaLinkedin } from 'react-icons/fa';
import { RequestSongIcon, VoteIcon, RateArtistIcon, ArtistProfileIcon, TipJarIcon, SupportArtistIcon } from '../assets/IconComponents';
import { useAuth } from '../contexts/AuthContext';
import FollowButton from '../components/FollowButton';

import {
  db,
  getArtistProfile,
  listenToLiveGig,
  hasUserVoted,
  recordUserVote,
  submitSongRequest,
  getAudienceGigHistory,
  computeAudienceBadges,
} from '../firebase-utils';

import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where,
} from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────
// SOCIAL FOLLOW BUTTONS
// ─────────────────────────────────────────────────────────────
const SocialFollowButtons = ({ socialMedia, artistName }) => {
  if (!socialMedia || !Object.values(socialMedia).some(v => v)) return null;
  const getSocialUrl = (platform, value) => {
    if (!value) return null;
    if (value.startsWith('http')) return value;
    switch (platform) {
      case 'instagram': return `https://instagram.com/${value.replace('@', '')}`;
      case 'facebook': return value.includes('facebook.com') ? value : `https://facebook.com/${value}`;
      case 'twitter': return `https://twitter.com/${value.replace('@', '')}`;
      case 'youtube': return value.includes('youtube.com') ? value : `https://youtube.com/${value}`;
      case 'spotify': return value.includes('spotify.com') ? value : `https://open.spotify.com/artist/${value}`;
      case 'tiktok': return `https://tiktok.com/@${value.replace('@', '')}`;
      case 'soundcloud': return `https://soundcloud.com/${value}`;
      default: return value;
    }
  };
  const platforms = [
    { key: 'instagram', label: '📸 Instagram', color: '#E1306C' },
    { key: 'facebook', label: '👍 Facebook', color: '#1877F2' },
    { key: 'youtube', label: '▶️ YouTube', color: '#FF0000' },
    { key: 'spotify', label: '🎵 Spotify', color: '#1DB954' },
    { key: 'twitter', label: '🐦 Twitter', color: '#1DA1F2' },
    { key: 'tiktok', label: '🎵 TikTok', color: '#000000' },
    { key: 'soundcloud', label: '☁️ SoundCloud', color: '#FF5500' },
  ];
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {platforms.map(({ key, label, color }) => {
        const value = socialMedia[key];
        if (!value) return null;
        const url = getSocialUrl(key, value);
        if (!url) return null;
        return (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer"
            className="social-btn text-xs" style={{ borderColor: color, color }}>{label}</a>
        );
      })}
    </div>
  );
};

export default function AudienceMode({
  liveGig,
  onModeChange,
  setShowAuthModal,
  hasRatedArtist: hasRatedArtistProp = false,
  setHasRatedArtist,
  followButton,
}) {
  const { currentUser } = useAuth();

  const [userVotes, setUserVotes] = useState({});
  const [hasRatedArtist, setHasRatedArtistLocal] = useState(false);
  const [ratingPromptShown, setRatingPromptShown] = useState(false);

  React.useEffect(() => {
    if (hasRatedArtistProp) setHasRatedArtistLocal(true);
  }, [hasRatedArtistProp]);

  const markHasRated = (val) => {
    setHasRatedArtistLocal(val);
    if (setHasRatedArtist) setHasRatedArtist(val);
  };

  const [liveGigData, setLiveGigData] = useState({
    votes: {}, queuedSongs: [], masterPlaylist: [], playedSongs: [],
    songRequests: [], requestsEnabled: true, songLimit: 20,
  });

  const [artistProfile, setArtistProfile] = useState(null);

  const [showVoteModal, setShowVoteModal] = useState(false);
  const [showArtistProfileModal, setShowArtistProfileModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [showGigPlaylistModal, setShowGigPlaylistModal] = useState(false);

  // ── Gig History state ──
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [gigHistory, setGigHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [badges, setBadges] = useState([]);

  const [selectedRequestSong, setSelectedRequestSong] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [playlistSearchQuery, setPlaylistSearchQuery] = useState('');
  const [artistRating, setArtistRating] = useState(0);
  const [currentGigRating, setCurrentGigRating] = useState(0);

  useEffect(() => {
    if (!artistProfile && liveGig?.artistId) {
      getArtistProfile(liveGig.artistId).then(profile => setArtistProfile(profile));
    }
  }, [liveGig?.artistId]);

  useEffect(() => {
    if (!liveGig?.id) return;
    const unsubscribe = listenToLiveGig(liveGig.id, (gigData) => {
      if (gigData.status === 'ended' || gigData.status === 'cancelled') {
        localStorage.removeItem(`GigWave_live_${currentUser?.uid}`);
        alert('🎸 The gig has ended! Thanks for joining.');
        onModeChange('discover');
        return;
      }
      setLiveGigData({
        votes: gigData.votes || {},
        queuedSongs: gigData.queuedSongs || [],
        masterPlaylist: gigData.masterPlaylist || [],
        playedSongs: gigData.playedSongs || [],
        songRequests: gigData.songRequests || [],
        requestsEnabled: gigData.requestsEnabled !== false,
        songLimit: gigData.songLimit || gigData.maxQueueSize || 20,
      });
    });
    return () => unsubscribe();
  }, [liveGig?.id]);

  useEffect(() => {
    if (!currentUser || !liveGig?.id) return;
    const checkRating = async () => {
      try {
        const gigRef = doc(db, 'liveGigs', String(liveGig.id));
        const gigSnap = await getDoc(gigRef);
        if (gigSnap.exists()) {
          const ratings = gigSnap.data().ratings || [];
          const userRating = ratings.find(r => r.userId === currentUser.uid);
          if (userRating) { markHasRated(true); setCurrentGigRating(userRating.artistRating); }
          else markHasRated(false);
        }
      } catch (error) { /* non-blocking */ }
    };
    checkRating();
  }, [currentUser, liveGig?.id]);

  // Load gig history when modal opens
  useEffect(() => {
    if (!showHistoryModal || !currentUser || historyLoaded) return;
    setLoadingHistory(true);
    getAudienceGigHistory(currentUser.uid)
      .then(history => {
        setGigHistory(history);
        setBadges(computeAudienceBadges(history));
        setHistoryLoaded(true);
      })
      .catch(console.error)
      .finally(() => setLoadingHistory(false));
  }, [showHistoryModal, currentUser]);

  const checkIfUserRatedArtist = async (artistId, userId) => {
    try {
      const q = query(collection(db, 'liveGigs'), where('artistId', '==', artistId));
      const snapshot = await getDocs(q);
      for (const gigDoc of snapshot.docs) {
        const ratings = gigDoc.data().ratings || [];
        if (ratings.some(r => r.userId === userId)) return true;
      }
      return false;
    } catch (error) { return false; }
  };

  const handleVote = async (songId) => {
    if (!currentUser) { alert('Please sign in to vote!'); setShowAuthModal(true); return; }
    if (liveGigData.playedSongs?.includes(songId)) { alert('⚠️ This song has already been played!'); return; }
    if (!liveGig) { alert('⚠️ No active live gig!'); return; }
    try {
      const alreadyVoted = await hasUserVoted(liveGig.id, songId, currentUser.uid);
      if (alreadyVoted) { alert('⚠️ You already voted for this song!'); return; }
      await recordUserVote(liveGig.id, songId, currentUser.uid);
      setUserVotes(prev => ({ ...prev, [songId]: true }));
      if (!hasRatedArtist && !ratingPromptShown && liveGig) {
        const hasRated = await checkIfUserRatedArtist(liveGig.artistId, currentUser.uid);
        if (!hasRated) { setRatingPromptShown(true); setShowRatingModal(true); }
        else markHasRated(true);
      }
      const gigRef = doc(db, 'liveGigs', String(liveGig.id));
      const gigSnap = await getDoc(gigRef);
      if (gigSnap.exists()) setLiveGigData(prev => ({ ...prev, votes: gigSnap.data().votes || {} }));
    } catch (error) { alert('❌ Error recording vote: ' + error.message); }
  };

  const sortAudienceQueue = (songs) => {
    const acceptedRequests = [], votedSongs = [], unvotedSongs = [];
    songs.forEach(song => {
      const isAcceptedRequest = liveGigData.songRequests?.some(r => r.songId === song.id && r.status === 'accepted');
      const voteCount = liveGigData.votes[Math.floor(song.id)] || 0;
      if (isAcceptedRequest) acceptedRequests.push(song);
      else if (voteCount > 0) votedSongs.push(song);
      else unvotedSongs.push(song);
    });
    const sortByVotes = (a, b) => {
      const vA = liveGigData.votes[Math.floor(a.id)] || 0;
      const vB = liveGigData.votes[Math.floor(b.id)] || 0;
      return vB - vA;
    };
    acceptedRequests.sort(sortByVotes);
    votedSongs.sort(sortByVotes);
    return [...acceptedRequests, ...votedSongs, ...unvotedSongs];
  };

  const handleSongRequest = async () => {
    if (!selectedRequestSong) { alert('Please select a song first!'); return; }
    if (!currentUser) { alert('Please log in to request a song'); setShowAuthModal(true); return; }
    const allRequests = liveGigData.songRequests || [];
    if (allRequests.some(r => r.requesterId === currentUser.uid && r.status === 'pending')) {
      alert('⚠️ You already have a pending request!'); return;
    }
    const myRequestForSong = allRequests.find(r => r.requesterId === currentUser.uid && r.songId === selectedRequestSong.id);
    if (myRequestForSong) {
      alert(myRequestForSong.status === 'rejected' ? `⚠️ "${selectedRequestSong.title}" was already rejected!` : `⚠️ You already requested "${selectedRequestSong.title}"!`);
      return;
    }
    const anyRequestForSong = allRequests.find(r => r.songId === selectedRequestSong.id && (r.status === 'pending' || r.status === 'accepted'));
    if (anyRequestForSong) {
      alert(anyRequestForSong.status === 'accepted' ? `⚠️ "${selectedRequestSong.title}" was already accepted!` : `⚠️ "${selectedRequestSong.title}" has already been requested!`);
      return;
    }
    if (allRequests.some(r => r.songId === selectedRequestSong.id && r.status === 'rejected')) {
      alert(`⚠️ "${selectedRequestSong.title}" was rejected by the artist!`); return;
    }
    if (liveGigData.playedSongs?.includes(selectedRequestSong.id)) { alert('⚠️ This song has already been played!'); return; }
    if (requestMessage.length > 200) { alert('Message must be 200 characters or less'); return; }
    try {
      await submitSongRequest(liveGig.id, selectedRequestSong, currentUser.uid, currentUser.displayName || 'Anonymous', requestMessage, 0, false);
      setShowRequestModal(false);
      setSelectedRequestSong(null);
      setRequestMessage('');
      setShowFollowModal(true);
    } catch (error) { alert('❌ Error: ' + error.message); }
  };

  const handleSubmitRating = async () => {
    if (!currentUser || !liveGig || artistRating === 0) return;
    try {
      const gigRef = doc(db, 'liveGigs', String(liveGig.id));
      const gigSnap = await getDoc(gigRef);
      if (gigSnap.exists()) {
        const currentRatings = gigSnap.data().ratings || [];
        const existingIndex = currentRatings.findIndex(r => r.userId === currentUser.uid);
        const ratingEntry = { userId: currentUser.uid, userName: currentUser.displayName || 'Anonymous', gigId: liveGig.id, artistRating, timestamp: new Date().toISOString() };
        const updatedRatings = existingIndex >= 0
          ? currentRatings.map((r, i) => i === existingIndex ? ratingEntry : r)
          : [...currentRatings, ratingEntry];
        await updateDoc(gigRef, { ratings: updatedRatings });
        markHasRated(true);
        setCurrentGigRating(artistRating);
        alert(existingIndex >= 0 ? '✅ Rating updated!' : '✅ Thanks for rating the artist!');
        setShowRatingModal(false);
        setArtistRating(0);
      }
    } catch (error) { alert('❌ Failed to submit rating'); }
  };

  const handleCloseRating = () => {
    setShowRatingModal(false);
    setArtistRating(0);
    setRatingPromptShown(true);
    markHasRated(true);
  };

  if (!liveGig) return null;

  return (
    <div className="rock-background min-h-screen p-4">
      <div className="max-w-4xl mx-auto">

        {/* Fixed Header */}
        <div className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-sm z-50 p-4 border-b border-electric/30">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="concert-heading text-xl tracking-wider" style={{ color: '#39FF14' }}>
                <span className="inline-block animate-pulse" style={{
                  filter: 'drop-shadow(0 0 8px #39FF14)',
                  textShadow: '0 0 10px #39FF14, 0 0 20px #39FF14'
                }}>🟢</span> LIVE NOW
              </span>
            </div>
            <h1 className="concert-heading text-3xl md:text-4xl text-white text-center">{liveGig.artistName}</h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="pt-32 pb-8">

          {/* View Gig Playlist Button */}
          <button
            onClick={() => setShowGigPlaylistModal(true)}
            className="w-3/4 mx-auto block bg-gradient-to-r from-cyan-500 via-purple-600 to-magenta-600 text-white py-3 md:py-4 px-8 rounded-xl font-bold text-lg md:text-xl hover:scale-105 transition-transform duration-200 mb-10 mt-12"
            style={{ boxShadow: '0 0 15px rgba(0,255,255,0.4), 0 0 30px rgba(255,0,255,0.3)', letterSpacing: '1px' }}>
            View Gig Playlist
          </button>

          {/* ROW 1: Request Song, Vote, Rate Artist */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <button onClick={() => { if (!currentUser) { alert('Please sign in to request a song!'); setShowAuthModal(true); return; } setShowRequestModal(true); }}
              className="aspect-square flex items-center justify-center hover:scale-105 transition-all duration-300 bg-transparent p-0 m-0 border-0">
              <RequestSongIcon />
            </button>
            <button onClick={() => { if (!currentUser) { alert('Please sign in to vote!'); setShowAuthModal(true); return; } setShowVoteModal(true); }}
              className="aspect-square flex items-center justify-center hover:scale-105 transition-all duration-300 bg-transparent p-0 m-0 border-0">
              <VoteIcon />
            </button>
            <button onClick={() => { if (!currentUser) { alert('Please sign in to rate the artist!'); setShowAuthModal(true); return; } setArtistRating(currentGigRating || 0); setShowRatingModal(true); }}
              className="aspect-square flex items-center justify-center hover:scale-105 transition-all duration-300 bg-transparent p-0 m-0 border-0">
              <RateArtistIcon />
            </button>
          </div>

          {/* ROW 2: Artist Profile, Tip Jar, Support */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <button onClick={() => setShowArtistProfileModal(true)}
              className="aspect-square flex items-center justify-center hover:scale-105 transition-all duration-300 bg-transparent p-0 m-0 border-0">
              <ArtistProfileIcon />
            </button>
            <button disabled className="aspect-square flex items-center justify-center bg-transparent p-0 m-0 border-0 opacity-50 cursor-not-allowed">
              <TipJarIcon />
            </button>
            <button disabled className="aspect-square flex items-center justify-center bg-transparent p-0 m-0 border-0 opacity-50 cursor-not-allowed">
              <SupportArtistIcon />
            </button>
          </div>

          {/* Follow on GigWave + My History */}
          {currentUser && (
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {/* GigWave Follow Button */}
              {liveGig?.artistId && (
                <div className="flex-1 flex items-center justify-center py-3 rounded-xl"
                  style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)' }}>
                  <div className="flex flex-col items-center gap-1">
                    <p className="text-purple-300 text-xs font-bold">Follow on GigWave</p>
                    <FollowButton
                      artistId={liveGig.artistId}
                      artistName={liveGig.artistName}
                      size="md"
                      showCount={true}
                      onAuthRequired={() => setShowAuthModal(true)}
                    />
                  </div>
                </div>
              )}
              {/* My Gig History */}
              <button
                onClick={() => setShowHistoryModal(true)}
                className="flex-1 py-3 rounded-xl font-bold text-sm hover:scale-105 transition-all"
                style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff' }}>
                📋 My Gig History
                {gigHistory.length > 0 && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ background: 'rgba(0,212,255,0.2)' }}>
                    {gigHistory.length} gigs
                  </span>
                )}
                {badges.length > 0 && (
                  <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>
                    {badges.length} badges
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Badge strip — shown when badges are earned */}
          {badges.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4"
              style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
              <span className="text-yellow-400 text-sm font-bold flex-shrink-0">🏅 Your badges</span>
              <div className="flex gap-1 flex-wrap">
                {badges.map(b => (
                  <span key={b.id} title={b.label} className="text-xl cursor-default">{b.emoji}</span>
                ))}
              </div>
              <button onClick={() => setShowHistoryModal(true)}
                className="ml-auto text-xs font-bold flex-shrink-0"
                style={{ color: '#fbbf24' }}>
                View all →
              </button>
            </div>
          )}

          {/* Social Media Section */}
          <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-2xl p-4 mb-4">
            <h3 className="text-center text-electric font-bold text-base mb-3 tracking-wider">🎸 CONNECT WITH THE ARTIST</h3>
            <div className="grid grid-cols-4 gap-3">
              {artistProfile?.socialMedia?.instagram ? (
                <a href={artistProfile.socialMedia.instagram.startsWith('http') ? artistProfile.socialMedia.instagram : `https://instagram.com/${artistProfile.socialMedia.instagram.replace('@', '')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="aspect-square bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(219,39,119,0.5)' }}>
                  <FaInstagram className="text-white text-3xl" />
                </a>
              ) : (
                <div className="aspect-square bg-gray-800/40 border-2 border-gray-600/30 rounded-xl flex items-center justify-center opacity-40">
                  <FaInstagram className="text-gray-500 text-3xl" />
                </div>
              )}
              {artistProfile?.socialMedia?.facebook ? (
                <a href={artistProfile.socialMedia.facebook.includes('facebook.com') ? artistProfile.socialMedia.facebook : `https://facebook.com/${artistProfile.socialMedia.facebook}`}
                  target="_blank" rel="noopener noreferrer"
                  className="aspect-square bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(37,99,235,0.5)' }}>
                  <FaFacebook className="text-white text-3xl" />
                </a>
              ) : (
                <div className="aspect-square bg-gray-800/40 border-2 border-gray-600/30 rounded-xl flex items-center justify-center opacity-40">
                  <FaFacebook className="text-gray-500 text-3xl" />
                </div>
              )}
              {artistProfile?.socialMedia?.youtube ? (
                <a href={artistProfile.socialMedia.youtube.includes('youtube.com') ? artistProfile.socialMedia.youtube : `https://youtube.com/${artistProfile.socialMedia.youtube}`}
                  target="_blank" rel="noopener noreferrer"
                  className="aspect-square bg-gradient-to-br from-red-600 to-red-800 rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(220,38,38,0.5)' }}>
                  <FaYoutube className="text-white text-3xl" />
                </a>
              ) : (
                <div className="aspect-square bg-gray-800/40 border-2 border-gray-600/30 rounded-xl flex items-center justify-center opacity-40">
                  <FaYoutube className="text-gray-500 text-3xl" />
                </div>
              )}
              {artistProfile?.socialMedia?.linkedin ? (
                <a href={artistProfile.socialMedia.linkedin.includes('linkedin.com') ? artistProfile.socialMedia.linkedin : `https://linkedin.com/in/${artistProfile.socialMedia.linkedin}`}
                  target="_blank" rel="noopener noreferrer"
                  className="aspect-square bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(29,78,216,0.5)' }}>
                  <FaLinkedin className="text-white text-3xl" />
                </a>
              ) : (
                <div className="aspect-square bg-gray-800/40 border-2 border-gray-600/30 rounded-xl flex items-center justify-center opacity-40">
                  <FaLinkedin className="text-gray-500 text-3xl" />
                </div>
              )}
            </div>
          </div>

          {/* Back Button */}
          <button onClick={() => onModeChange('discover')}
            className="w-full py-4 bg-gradient-to-r from-gray-800 to-gray-900 border-2 border-gray-700 rounded-xl text-white font-bold hover:scale-105 transition-all duration-300"
            style={{ boxShadow: '0 0 15px rgba(107,114,128,0.3)' }}>
            ← Back to Discovery
          </button>
        </div>
      </div>

      {/* ══════════ MODAL: GIG HISTORY ══════════ */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="gig-card max-w-lg w-full border-2 border-electric max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-white">📋 My Gig History</h2>
                <p className="text-gray-400 text-xs mt-0.5">Every GigWave gig you've attended</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-white hover:text-neon text-2xl leading-none">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">

              {/* ── Badges shelf ── */}
              {badges.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">🏅 Your Badges</p>
                  <div className="grid grid-cols-2 gap-2">
                    {badges.map(badge => (
                      <div key={badge.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                        style={{ background: `${badge.color}12`, border: `1px solid ${badge.color}35` }}>
                        <span className="text-2xl flex-shrink-0">{badge.emoji}</span>
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-white leading-tight">{badge.label}</p>
                          <p className="text-xs leading-tight" style={{ color: badge.color }}>{badge.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10" />
                </div>
              )}

              {loadingHistory && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-electric mx-auto mb-3"></div>
                  <p className="text-electric font-bold">Loading your history...</p>
                </div>
              )}

              {!loadingHistory && gigHistory.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-5xl mb-4">🎸</p>
                  <p className="text-white font-bold text-lg mb-2">No gigs yet!</p>
                  <p className="text-gray-400 text-sm">When you join live gigs on GigWave, they'll appear here.</p>
                </div>
              )}

              {!loadingHistory && gigHistory.length > 0 && (
                <div className="space-y-4">
                  {/* Summary pill */}
                  <div className="flex gap-3 flex-wrap">
                    <span className="text-xs px-3 py-1.5 rounded-full font-bold"
                      style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}>
                      🎸 {gigHistory.length} {gigHistory.length === 1 ? 'gig' : 'gigs'} attended
                    </span>
                    {gigHistory.some(g => g.myTipTotal > 0) && (
                      <span className="text-xs px-3 py-1.5 rounded-full font-bold"
                        style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>
                        💰 ₹{gigHistory.reduce((s, g) => s + g.myTipTotal, 0).toLocaleString()} total tipped
                      </span>
                    )}
                  </div>

                  {gigHistory.map(gig => (
                    <div key={gig.id} className="rounded-xl overflow-hidden"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>

                      {/* Gig header */}
                      <div className="p-4 border-b border-white/8">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-white font-bold text-base">{gig.artistName}</p>
                            <p className="text-electric text-sm font-semibold">📍 {gig.venueName}</p>
                            <p className="text-gray-500 text-xs mt-0.5">
                              📅 {gig.gigDate}{gig.gigTime ? ` at ${gig.gigTime}` : ''}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {gig.myRating && (
                              <span className="text-xs px-2 py-1 rounded-full font-bold"
                                style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' }}>
                                ⭐ {gig.myRating}/5
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              👥 {gig.totalAudience} audience
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* My activity */}
                      <div className="p-4 space-y-3">
                        {/* Requests */}
                        {gig.myRequests.length > 0 ? (
                          <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">🎵 Your Requests</p>
                            <div className="space-y-1">
                              {gig.myRequests.map((req, i) => (
                                <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                  style={{ background: 'rgba(255,255,255,0.04)' }}>
                                  <div>
                                    <p className="text-white text-sm font-semibold">{req.title}</p>
                                    <p className="text-gray-400 text-xs">{req.artist}</p>
                                  </div>
                                  <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                    style={{
                                      background: req.status === 'accepted' ? 'rgba(34,197,94,0.15)' : req.status === 'rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                                      color: req.status === 'accepted' ? '#22c55e' : req.status === 'rejected' ? '#ef4444' : '#fbbf24',
                                    }}>
                                    {req.status === 'accepted' ? '✅ Accepted' : req.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-600 text-xs italic">No song requests made</p>
                        )}

                        {/* Tip */}
                        {gig.myTipTotal > 0 && (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                            <span className="text-green-400 text-sm">💰</span>
                            <p className="text-green-400 text-sm font-bold">
                              Tipped ₹{gig.myTipTotal.toLocaleString()} ({gig.tipCount}x)
                            </p>
                          </div>
                        )}

                        {/* No activity */}
                        {gig.myRequests.length === 0 && !gig.myTipTotal && !gig.myRating && (
                          <p className="text-gray-600 text-xs italic">You attended this gig</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-white/10 flex-shrink-0">
              <button onClick={() => setShowHistoryModal(false)} className="w-full btn btn-electric text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: VOTE ══════════ */}
      {showVoteModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="gig-card max-w-md w-full border-2 border-magenta relative max-h-[85vh] flex flex-col">
            <button onClick={() => setShowVoteModal(false)}
              className="absolute top-0 right-0 text-white hover:text-neon transition font-bold text-3xl leading-none z-20 p-2" style={{ fontWeight: '900' }}>×</button>
            <div className="px-6 pt-8 pb-1">
              <h2 className="concert-heading text-base sm:text-xl text-magenta text-center flex items-center justify-center gap-2">
                <span className="text-2xl sm:text-3xl">⚡</span><span>Vote For Songs</span>
              </h2>
              <p className="text-gray-light text-xs sm:text-sm mt-1 text-center">Vote For Song To Prioritize It Or Add To Song Queue</p>
            </div>
            <div className="px-6 pb-3 pt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input type="text" placeholder="Search songs or artists..."
                  value={playlistSearchQuery} onChange={(e) => setPlaylistSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-black/50 border border-magenta/30 rounded-lg text-white placeholder-gray-500 focus:border-magenta focus:outline-none text-sm" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {(() => {
                const queued = (liveGigData.queuedSongs || []).filter(s => !liveGigData.playedSongs?.includes(s.id));
                const masterExtras = (liveGigData.masterPlaylist || liveGig?.masterPlaylist || []).filter(s => {
                  return !queued.some(q => q.id === s.id) && !liveGigData.playedSongs?.includes(s.id);
                });
                const allSongs = [...queued, ...masterExtras];
                const filtered = allSongs.filter(song =>
                  !playlistSearchQuery ||
                  song.title.toLowerCase().includes(playlistSearchQuery.toLowerCase()) ||
                  song.artist.toLowerCase().includes(playlistSearchQuery.toLowerCase())
                );
                const sorted = sortAudienceQueue(filtered);
                if (sorted.length === 0) return <div className="text-center py-8"><p className="text-gray-400 text-sm">No songs available for voting</p></div>;
                return (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-electric flex items-center gap-2"><span>📋</span><span>Tonight's Queue</span></h3>
                      <p className="text-[9px] text-gray-400 italic">Tap to vote · sorted by votes</p>
                    </div>
                    <div className="space-y-2">
                      {sorted.map((song, index) => {
                        const voteCount = liveGigData.votes[Math.floor(song.id)] || 0;
                        const hasVoted = userVotes && userVotes[song.id];
                        const isRequested = liveGigData.songRequests?.some(r => r.songId === song.id && r.status === 'accepted');
                        const truncatedTitle = song.title.split(' ').slice(0, 5).join(' ') + (song.title.split(' ').length > 5 ? '...' : '');
                        const truncatedArtist = song.artist.split(' ').slice(0, 2).join(' ') + (song.artist.split(' ').length > 2 ? '...' : '');
                        const rowClass = isRequested
                          ? 'bg-amber-500/15 border border-amber-400/50 hover:bg-amber-500/25'
                          : voteCount > 0
                            ? 'bg-magenta/10 border border-magenta/30 hover:bg-magenta/20'
                            : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-magenta';
                        return (
                          <button key={song.id} onClick={() => handleVote(song.id)} disabled={hasVoted}
                            className={`w-full p-2 rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed ${rowClass}`}>
                            <div className="flex items-center gap-2 w-full">
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] ${isRequested ? 'bg-amber-400/30 text-amber-300' : 'bg-electric/30 text-electric'}`}>
                                {isRequested ? '🎵' : index + 1}
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <div className="flex items-center gap-1">
                                  <p className={`font-bold text-[11px] leading-tight ${isRequested ? 'text-amber-200' : 'text-white'}`}>{truncatedTitle}</p>
                                  {isRequested && <span className="text-[8px] text-amber-400 font-bold bg-amber-400/20 px-1 rounded">REQ</span>}
                                </div>
                                <p className="text-gray-400 text-[9px] leading-tight">{truncatedArtist}</p>
                              </div>
                              {voteCount > 0 && <div className="flex-shrink-0 flex items-center gap-0.5 pl-4"><span className="text-magenta text-[10px]">⚡</span><span className="font-bold text-[10px] text-magenta">{voteCount}</span></div>}
                              {hasVoted && <span className="text-[9px] text-electric ml-1">✓</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t border-white/10">
              <p className="text-gray-400 text-xs text-center">
                {userVotes && Object.keys(userVotes).length > 0
                  ? `You've Voted For ${Object.keys(userVotes).length} Song${Object.keys(userVotes).length > 1 ? 's' : ''}`
                  : 'Tap Any Song To Vote'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: ARTIST PROFILE ══════════ */}
      {showArtistProfileModal && artistProfile && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="rock-background gig-card border-2 border-electric max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-black/95 px-6 pt-4 pb-2 border-b border-white/10 z-10">
              <div className="flex justify-end mb-2">
                <button onClick={() => setShowArtistProfileModal(false)} className="text-white hover:text-neon transition font-bold text-3xl leading-none" style={{ fontWeight: '900' }}>×</button>
              </div>
              <h2 className="concert-heading text-base sm:text-2xl text-electric text-center flex items-center justify-center gap-2 pb-3">
                <span className="text-2xl sm:text-3xl">🎸</span><span>Artist Profile</span>
              </h2>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <h1 className="concert-heading text-2xl sm:text-4xl text-white mb-3">{artistProfile.artistName}</h1>
                <div className="flex flex-wrap justify-center gap-3 mb-4">
                  <span className="px-4 py-2 bg-magenta/20 text-magenta rounded-full text-sm font-bold border border-magenta">🎵 {artistProfile.genre}</span>
                  <span className="px-4 py-2 bg-electric/20 text-electric rounded-full text-sm font-bold border border-electric">📍 {artistProfile.location}</span>
                </div>
                {/* GigWave Follow Button in profile modal */}
                {liveGig?.artistId && (
                  <div className="flex justify-center">
                    <FollowButton
                      artistId={liveGig.artistId}
                      artistName={artistProfile.artistName}
                      size="lg"
                      showCount={true}
                      onAuthRequired={() => { setShowArtistProfileModal(false); setShowAuthModal(true); }}
                    />
                  </div>
                )}
              </div>
              <div className="mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-electric mb-3 text-center">About</h3>
                <p className="text-gray-light leading-relaxed text-left">{artistProfile.bio}</p>
              </div>
              {artistProfile.socialMedia && (
                <div className="mb-6"><SocialFollowButtons socialMedia={artistProfile.socialMedia} artistName={artistProfile.artistName} /></div>
              )}
              <button disabled className="w-full py-4 bg-gradient-to-r from-neon/20 to-electric/20 border-2 border-neon/50 text-white rounded-xl font-bold text-lg opacity-50 cursor-not-allowed">
                <Heart size={24} className="inline mr-2" />Support Artist (Coming Soon)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: REQUEST SONG ══════════ */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="gig-card max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-magenta">
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1 text-center">
                <h2 className="concert-heading text-3xl text-magenta">Request A Song</h2>
                <p className="text-gray-light text-sm mt-2">Send Your Song Request To The Artist</p>
              </div>
              <button onClick={() => { setShowRequestModal(false); setSelectedRequestSong(null); setRequestMessage(''); }} className="text-white hover:text-neon transition"><X size={32} /></button>
            </div>
            <div className="mb-6 p-4 bg-blue-500/20 border border-blue-400 rounded-lg">
              <h4 className="text-blue-300 font-bold mb-3 flex items-center gap-2"><span>📊</span><span>Queue Status</span></h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><p className="text-gray-light">Queue Capacity</p><p className="text-white font-bold text-lg">{Math.min(liveGigData.queuedSongs?.length || 0, liveGigData.songLimit || 20)}/{liveGigData.songLimit || 20}</p></div>
                <div><p className="text-gray-light">Songs Played</p><p className="text-white font-bold text-lg">{liveGigData.playedSongs?.length || 0}</p></div>
                <div><p className="text-gray-light">Songs Left</p><p className="text-white font-bold text-lg">{Math.min((liveGigData.queuedSongs || []).filter(s => !liveGigData.playedSongs?.includes(s.id)).length, liveGigData.songLimit || 20)}</p></div>
                <div><p className="text-gray-light">Pending Requests</p><p className="text-white font-bold text-lg">{(liveGigData.songRequests || []).filter(r => r.status === 'pending').length}</p></div>
              </div>
            </div>
            <div className="mb-4">
              <input type="text" placeholder="🔍 Search by song or artist name..."
                value={playlistSearchQuery} onChange={(e) => setPlaylistSearchQuery(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-magenta/30 focus:border-magenta focus:outline-none" />
            </div>
            <div className="mb-6">
              <h3 className="text-white font-bold text-xl mb-3">Select A Song</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto bg-black/30 rounded-lg p-3 border border-white/10">
                {(() => {
                  const queued = (liveGigData.queuedSongs || []).filter(s => !liveGigData.playedSongs?.includes(s.id));
                  const masterExtras = (liveGigData.masterPlaylist || liveGig?.masterPlaylist || []).filter(s => !queued.some(q => q.id === s.id) && !liveGigData.playedSongs?.includes(s.id));
                  const allSongs = sortAudienceQueue([...queued, ...masterExtras]).filter(song =>
                    !playlistSearchQuery || song.title.toLowerCase().includes(playlistSearchQuery.toLowerCase()) || song.artist.toLowerCase().includes(playlistSearchQuery.toLowerCase())
                  );
                  if (allSongs.length === 0) return <div className="text-center py-8 text-gray-light">No songs available</div>;
                  return allSongs.map((song, index) => {
                    const requests = liveGigData.songRequests || [];
                    const isInQueue = queued.some(q => q.id === song.id);
                    const isPlayed = liveGigData.playedSongs?.includes(song.id);
                    const songRequest = requests.find(r => r.songId === song.id);
                    const isAccepted = songRequest?.status === 'accepted';
                    const isPending = songRequest?.status === 'pending';
                    const isRejected = songRequest?.status === 'rejected';
                    const myRequest = requests.find(r => r.songId === song.id && r.requesterId === currentUser?.uid);
                    const voteCount = liveGigData.votes[Math.floor(song.id)] || 0;
                    const truncateTitle = (t) => { const w = t.split(' '); return w.length <= 5 ? t : w.slice(0, 5).join(' ') + '...'; };
                    const isSelected = selectedRequestSong?.id === song.id;
                    const cannotRequest = isPlayed || isAccepted || isPending || isRejected || myRequest;
                    const rowClass = isSelected ? 'bg-magenta/30 border-magenta cursor-pointer' : isRejected ? 'bg-red-900/20 border-red-800/40 opacity-50 cursor-not-allowed' : isPlayed ? 'bg-white/5 border-white/5 opacity-40 cursor-not-allowed' : isPending ? 'bg-yellow-500/10 border-yellow-500/40 cursor-not-allowed' : isAccepted ? 'bg-amber-500/15 border-amber-400/50 cursor-not-allowed' : 'bg-white/8 border-electric/30 hover:border-electric/60 cursor-pointer';
                    const handleClick = () => { if (isRejected || isPlayed || isPending || isAccepted || myRequest) return; setSelectedRequestSong(song); };
                    let badge = null;
                    if (isPlayed) badge = <span className="text-[8px] text-gray-500 font-bold bg-gray-700/40 px-1 rounded flex-shrink-0">PLAYED</span>;
                    else if (isRejected) badge = <span className="text-[8px] text-red-400 font-bold bg-red-900/30 px-1 rounded flex-shrink-0">REJECTED</span>;
                    else if (isAccepted) badge = <span className="text-[8px] text-amber-400 font-bold bg-amber-400/20 px-1 rounded flex-shrink-0">ACCEPTED</span>;
                    else if (isPending) badge = <span className="text-[8px] text-yellow-400 font-bold bg-yellow-400/20 px-1 rounded flex-shrink-0">PENDING</span>;
                    else if (isInQueue) badge = <span className="text-[8px] text-electric/70 font-bold bg-electric/10 px-1 rounded flex-shrink-0">IN QUEUE</span>;
                    return (
                      <div key={song.id} onClick={handleClick} className={`p-3 rounded-lg transition border-2 ${rowClass}`}>
                        <div className="flex items-center gap-2">
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] ${isAccepted ? 'bg-amber-400/30 text-amber-300' : isRejected ? 'bg-red-900/30 text-red-400' : 'bg-electric/30 text-electric'}`}>
                            {isRejected ? '✕' : isAccepted ? '🎵' : index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className={`font-bold truncate text-sm ${isAccepted ? 'text-amber-200' : isRejected || isPlayed ? 'text-gray-500' : 'text-white'}`}>{truncateTitle(song.title)}</span>
                              {badge}
                            </div>
                            <div className="text-gray-400 text-xs">{song.artist}{voteCount > 0 ? <span className="text-magenta ml-2">⚡{voteCount}</span> : ''}</div>
                          </div>
                          {isSelected && <div className="text-magenta text-2xl flex-shrink-0">✓</div>}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            {selectedRequestSong && (
              <div className="mb-6 p-4 bg-magenta/20 border-2 border-magenta rounded-lg">
                <p className="text-magenta font-bold text-sm mb-2">SELECTED SONG:</p>
                <h4 className="text-white font-bold text-xl">{selectedRequestSong.title}</h4>
                <p className="text-electric">{selectedRequestSong.artist}</p>
              </div>
            )}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-white font-bold">💌 Personal Message <span className="text-gray-400 font-normal text-sm">(optional)</span></label>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: requestMessage.length > 150 ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)', color: requestMessage.length > 150 ? '#f87171' : '#9ca3af' }}>
                  {requestMessage.length}/200
                </span>
              </div>
              <textarea value={requestMessage} onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="e.g. This one's for my girlfriend's birthday 🎂  •  We drove 2 hours to be here! 🙌  •  Can you play this one for us? 🙏"
                maxLength={200}
                className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 border focus:outline-none resize-none text-sm"
                style={{ background: 'rgba(219,39,119,0.06)', border: `1px solid ${requestMessage.length > 0 ? 'rgba(219,39,119,0.5)' : 'rgba(255,255,255,0.15)'}`, minHeight: '80px' }}
                rows={3} />
              {requestMessage.length > 0 && (
                <div className="mt-2 px-3 py-2 rounded-lg flex items-start gap-2"
                  style={{ background: 'rgba(219,39,119,0.08)', border: '1px solid rgba(219,39,119,0.25)' }}>
                  <span className="text-magenta text-sm flex-shrink-0">💌</span>
                  <p className="text-white text-xs italic leading-relaxed">"{requestMessage}"</p>
                </div>
              )}
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <button onClick={handleSongRequest} disabled={!selectedRequestSong}
                className={`flex-1 py-4 rounded-lg font-bold text-lg transition ${selectedRequestSong ? 'btn btn-neon' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}>
                {selectedRequestSong ? 'Send Request' : 'Select a song first'}
              </button>
              <button onClick={() => { setShowRequestModal(false); setSelectedRequestSong(null); setRequestMessage(''); }}
                className="px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold transition">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: FOLLOW + RATE (after request) ══════════ */}
      {showFollowModal && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="gig-card border-2 border-electric w-full max-w-sm flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h2 className="concert-heading text-lg text-electric">✅ Request Sent!</h2>
              <button onClick={() => setShowFollowModal(false)} className="text-white hover:text-neon text-2xl leading-none transition">✕</button>
            </div>
            <div className="px-5 py-5 space-y-5">
              {artistProfile?.socialMedia && Object.values(artistProfile.socialMedia).some(v => v) && (
                <div>
                  <p className="text-white font-bold text-base mb-3 text-center">Follow The Artist</p>
                  <div className="flex flex-col gap-2">
                    {artistProfile.socialMedia.instagram && (
                      <a href={artistProfile.socialMedia.instagram.startsWith('http') ? artistProfile.socialMedia.instagram : `https://instagram.com/${artistProfile.socialMedia.instagram.replace('@','')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white text-sm transition hover:opacity-80"
                        style={{ background: 'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' }}>
                        <span style={{ fontSize: '1.2rem' }}>📸</span> Follow on Instagram
                      </a>
                    )}
                    {artistProfile.socialMedia.youtube && (
                      <a href={artistProfile.socialMedia.youtube.includes('youtube.com') ? artistProfile.socialMedia.youtube : `https://youtube.com/${artistProfile.socialMedia.youtube}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white text-sm transition hover:opacity-80"
                        style={{ background: '#FF0000' }}>
                        <span style={{ fontSize: '1.2rem' }}>▶️</span> Subscribe on YouTube
                      </a>
                    )}
                    {artistProfile.socialMedia.spotify && (
                      <a href={artistProfile.socialMedia.spotify.includes('spotify.com') ? artistProfile.socialMedia.spotify : `https://open.spotify.com/artist/${artistProfile.socialMedia.spotify}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white text-sm transition hover:opacity-80"
                        style={{ background: '#1DB954' }}>
                        <span style={{ fontSize: '1.2rem' }}>🎵</span> Follow on Spotify
                      </a>
                    )}
                  </div>
                  {/* GigWave follow */}
                  {liveGig?.artistId && (
                    <div className="mt-3 flex justify-center">
                      <FollowButton artistId={liveGig.artistId} artistName={liveGig.artistName} size="md" showCount={true} onAuthRequired={() => setShowAuthModal(true)} />
                    </div>
                  )}
                </div>
              )}
              {artistProfile?.socialMedia && Object.values(artistProfile.socialMedia).some(v => v) && (
                <div className="border-t border-white/10" />
              )}
              {!ratingPromptShown ? (
                <div className="text-center">
                  <p className="text-white font-bold text-base mb-1">Rate The Artist</p>
                  <p className="text-gray-400 text-xs mb-3">How's the performance so far?</p>
                  <div className="flex justify-center gap-2 mb-4">
                    {[1,2,3,4,5].map(star => (
                      <button key={star} onClick={() => setArtistRating(star)}
                        style={{ fontSize: '2rem', width: '2.5rem', height: '2.5rem', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>
                        {star <= artistRating ? '⭐' : '☆'}
                      </button>
                    ))}
                  </div>
                  <button disabled={artistRating === 0}
                    onClick={async () => {
                      if (artistRating === 0) return;
                      try {
                        const { doc, updateDoc, arrayUnion, arrayRemove, getDoc } = await import('firebase/firestore');
                        const { db } = await import('../firebase');
                        const gigRef = doc(db, 'liveGigs', liveGig.id);
                        const snap = await getDoc(gigRef);
                        const existing = (snap.data()?.ratings || []);
                        const prev = existing.find(r => r.userId === currentUser.uid);
                        if (prev) await updateDoc(gigRef, { ratings: arrayRemove(prev) });
                        await updateDoc(gigRef, { ratings: arrayUnion({ userId: currentUser.uid, rating: artistRating, timestamp: new Date().toISOString() }) });
                        setHasRatedArtistLocal(true);
                        setCurrentGigRating(artistRating);
                        setRatingPromptShown(true);
                        setShowFollowModal(false);
                      } catch(e) { alert('Error saving rating: ' + e.message); }
                    }}
                    className="btn btn-electric w-full flex items-center justify-center gap-2"
                    style={{ opacity: artistRating === 0 ? 0.5 : 1 }}>
                    <span>⭐</span><span>Submit Rating</span>
                  </button>
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-electric text-sm font-bold">⭐ Thanks for rating the artist!</p>
                </div>
              )}
            </div>
            <div className="px-5 pb-5">
              <button onClick={() => setShowFollowModal(false)} className="btn btn-ghost w-full">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: RATE ARTIST ══════════ */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="gig-card max-w-sm w-full border-2 border-electric relative overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/10">
              <h2 className="concert-heading text-lg text-electric flex items-center gap-2"><span>⭐</span><span>Rate The Artist</span></h2>
              <button onClick={handleCloseRating} className="text-white hover:text-neon transition text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-gray-light text-xs text-center">How's the performance? (Optional)</p>
              {hasRatedArtist && (
                <div className="p-2 bg-neon/20 border border-neon rounded-lg">
                  <p className="text-neon font-bold text-xs text-center">✅ You rated {currentGigRating}★ — tap stars to update</p>
                </div>
              )}
              <p className="text-white text-base font-bold text-center">{liveGig?.artistName || 'Artist'}</p>
              <div className="flex justify-center items-center gap-2 py-2">
                {[1,2,3,4,5].map(star => (
                  <button key={star} onClick={() => setArtistRating(star)}
                    style={{ fontSize: '2rem', lineHeight: 1, width: '2.5rem', height: '2.5rem' }}
                    className={`flex items-center justify-center transition-transform ${artistRating >= star ? 'scale-110' : 'hover:scale-105'}`}>
                    {artistRating >= star ? '⭐' : '☆'}
                  </button>
                ))}
              </div>
              {artistRating > 0 && (
                <p className="text-center text-electric font-bold text-sm">
                  {['','Poor','Fair','Good','Great','Excellent'][artistRating]}
                </p>
              )}
            </div>
            <div className="px-5 pb-5 space-y-3">
              <div className="flex gap-3">
                <button onClick={handleSubmitRating} disabled={artistRating === 0}
                  className={`flex-1 btn text-sm ${artistRating === 0 ? 'btn-ghost opacity-50 cursor-not-allowed' : 'btn-neon'}`}>
                  <span>✓</span><span>{hasRatedArtist ? 'Update Rating' : 'Submit Rating'}</span>
                </button>
                <button onClick={handleCloseRating} className="btn btn-ghost text-sm">Close</button>
              </div>
              <p className="text-center text-gray-light text-xs">Your rating helps the artist improve!</p>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: VIEW GIG PLAYLIST ══════════ */}
      {showGigPlaylistModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="gig-card max-w-md w-full border-2 border-electric max-h-[85vh] flex flex-col">
            <button onClick={() => setShowGigPlaylistModal(false)}
              className="absolute top-0 right-0 text-white hover:text-neon transition font-bold text-3xl leading-none z-20 p-2" style={{ fontWeight: '900' }}>×</button>
            <div className="px-3 pt-4 pb-1">
              <h2 className="text-[11px] sm:text-sm font-bold text-electric text-center flex items-center justify-center gap-1">
                <span className="text-sm sm:text-lg">🎵</span><span className="whitespace-nowrap">Tonight's Playlist</span>
              </h2>
              <p className="text-gray-light text-[8px] sm:text-[10px] mt-0.5 text-center truncate">{liveGig?.artistName} • {liveGig?.venueName}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {liveGigData.queuedSongs && liveGigData.queuedSongs.length > 0 ? (
                <div className="space-y-2">
                  {liveGigData.queuedSongs
                    .filter(song => !liveGigData.playedSongs?.includes(song.id))
                    .map(song => ({ ...song, voteCount: liveGigData.votes[Math.floor(song.id)] || 0 }))
                    .sort((a, b) => b.voteCount - a.voteCount)
                    .map((song, index) => {
                      const truncatedTitle = song.title.split(' ').slice(0, 5).join(' ') + (song.title.split(' ').length > 5 ? '...' : '');
                      const truncatedArtist = song.artist.split(' ').slice(0, 2).join(' ') + (song.artist.split(' ').length > 2 ? '...' : '');
                      return (
                        <div key={song.id} className="bg-white/5 border border-white/10 rounded-lg p-2 hover:bg-white/10 transition">
                          <div className="flex items-center gap-2 w-full">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] bg-electric/30 text-electric">{index + 1}</div>
                            <div className="flex-1 min-w-0 text-left">
                              <p className="font-bold text-[11px] leading-tight text-white">{truncatedTitle}</p>
                              <p className="text-gray-400 text-[9px] leading-tight">{truncatedArtist}</p>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-0.5 pl-4">
                              <span className="text-magenta text-[10px]">⚡</span>
                              <span className="font-bold text-[10px] text-magenta">{song.voteCount}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-12"><p className="text-gray-light text-sm">No songs in the playlist yet</p></div>
              )}
              {liveGigData.playedSongs && liveGigData.playedSongs.length > 0 && (
                <div className="mt-6 pt-4 border-t border-white/20">
                  <h3 className="text-gray-400 font-bold text-sm mb-3 flex items-center gap-2"><span>✅</span><span>Already Played ({liveGigData.playedSongs.length})</span></h3>
                  <div className="space-y-2 opacity-60">
                    {liveGigData.queuedSongs.filter(song => liveGigData.playedSongs.includes(song.id)).map(song => {
                      const voteCount = liveGigData.votes[Math.floor(song.id)] || 0;
                      const truncatedTitle = song.title.split(' ').slice(0, 5).join(' ') + (song.title.split(' ').length > 5 ? '...' : '');
                      const truncatedArtist = song.artist.split(' ').slice(0, 2).join(' ') + (song.artist.split(' ').length > 2 ? '...' : '');
                      return (
                        <div key={song.id} className="bg-gray-500/20 border border-gray-600 rounded-lg p-2">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-[10px]">✓</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-400 font-semibold text-[10px] line-through leading-tight">{truncatedTitle}</p>
                              <p className="text-gray-500 text-[9px] leading-tight">{truncatedArtist}</p>
                            </div>
                            {voteCount > 0 && <div className="flex-shrink-0 flex items-center gap-0.5"><span className="text-gray-600 text-[9px]">⚡</span><span className="text-gray-600 text-[9px]">{voteCount}</span></div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-white/10">
              <button onClick={() => setShowGigPlaylistModal(false)} className="w-full btn btn-electric text-sm sm:text-base">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
